import { yupResolver } from '@hookform/resolvers/yup'
import axios from 'axios'
import { useRouter } from 'next/router'
import { Button, Input } from 'components/atomic'
import { sendgridValidEmailScoreThreshold } from 'lib/constants'
import nhost from 'lib/nhost-client'
import { useMemo, useState } from 'react'
import { useEffect } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import { LoadingSpinner } from 'components/delayed'

const formSchema = yup.object().shape({
  email: yup.string().email('Email must be a valid email').required('Email is required'),
})

interface formData extends HTMLFormControlsCollection {
  email: string
}

interface EmailVerifiedProps {
  isSmallScreen: boolean
  userEmail: string
}

const EmailVerified: React.FunctionComponent<EmailVerifiedProps> = ({ isSmallScreen, userEmail }) => {
  const router = useRouter()
  const [serverError, setServerError] = useState('')
  const [completed, setCompleted] = useState(false)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({
    defaultValues: useMemo(() => {
      return { email: userEmail ?? '' }
    }, [userEmail]),
    resolver: yupResolver(formSchema),
  })
  const formValues = useWatch({ control })
  const [isLoading, setIsLoading] = useState(false)

  // Request validate email
  const validateEmail = async (email: string) => {
    try {
      const response = await axios.post(
        'https://api.sendgrid.com/v3/validations/email',
        {
          email: email,
          source: 'signup',
        },
        {
          headers: {
            Authorization: `Bearer ${process.env.NEXT_PUBLIC_SENDGRID_EMAIL_VALIDATION_KEY}`,
          },
        },
      )
      return { status: 1, score: response.data?.result?.score || 1 }
    } catch (error) {
      console.log(error)
      return { status: 0 }
    }
  }

  // Set email detail
  const setEmail = async (email: string) => {
    try {
      await axios.get(`/api/update-user-email?email=${encodeURIComponent(email)}`)
      return { status: 1 }
    } catch (error) {
      if (error.response) {
        const errors = error.response.data.response.errors
        if (errors.filter((err) => err.message.includes('duplicate'))) {
          setError('email', { type: 'custom', message: 'Email already exists' })
        }
      } else {
        setServerError(error.message)
      }
      return { status: 0 }
    }
  }

  useEffect(() => {
    setServerError('')
    setCompleted(false)
  }, [formValues])

  const onSubmit = async (data: formData) => {
    setIsLoading(true)
    // Verify email
    const result = await validateEmail(data.email)
    if (result.status == 1) {
      if (result.score < sendgridValidEmailScoreThreshold) {
        setIsLoading(false)
        setServerError("Email doesn't look correct. Fix any typo")
        return
      }
    }

    // Submit update user
    const res = await setEmail(data.email)
    if (res.status === 1) {
      setIsLoading(false)
      alert("You'll be redirected to the Login page. Click Send Link with your email to verify it.")

      // Already signed in, Logout first
      await nhost.auth.signOut()
      if (router.asPath === '/general-profile') {
        window.location.href = `/login?email=${encodeURIComponent(data.email)}`
      }
    } else {
      setIsLoading(false)
    }
  }

  // Render UI
  return (
    <>
      <div className={`${isSmallScreen ? 'sm:mt-5 md:mt-0 lg:mt-0 lg:border' : 'border'} mb-8 border-[#454340] px-10`}>
        <LoadingSpinner isShown={isLoading} className="bg-black/90" />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`${isSmallScreen ? 'lg:flex lg:items-center' : 'flex items-center'} py-9`}>
            <div className="mr-5">
              <Input
                {...register('email', { required: true })}
                rtlDirection={true}
                type="email"
                label=""
                placeholder="Enter email"
                error={errors.email?.message}
              />
            </div>

            {/* Button submit */}
            <div className={`${isSmallScreen ? 'mt-3 lg:mt-0' : 'mr-2'}`}>
              <Button variant="primary" className="outline-none" state={isSubmitting ? 'disabled' : ''}>
                Verify
              </Button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default EmailVerified
